import { stripHTML } from "@decocms/apps-website/utils/html";
import ProductCard from "~/components/product/ProductCard";
import Icon from "~/components/ui/Icon";
import Slider from "~/components/ui/Slider";
import SliderJS from "~/components/ui/SliderJS";
import TimerJS from "~/components/ui/TimerJS";
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice";
import { relative } from "~/sdk/relative";
import { urlify } from "~/sdk/urlify";
import { useId } from "react";
import { useOffer } from "~/sdk/useOffer";
import trimProductForShelf from "~/sdk/trimProductForShelf";
import { useVariantPossibilities } from "@decocms/apps-commerce/sdk/useVariantPossibilities";
import type { ImageWidget } from "~/types/widgets";
import { HTMLWidget } from "~/types/widgets";
import type { Product, ViewItemListParams } from "@decocms/apps-commerce/types";
import Image from "~/components/ui/Image";

import { SendEventOnView } from "~/components/Analytics";
import Title from "~/components/ui/Title";
import { TitleIcon } from "~/loaders/TitleIcon";
import { clsx } from "~/sdk/clx";
import { isSchedulingActive, type Scheduling } from "~/sdk/contentScheduling";
import BackgroundPicture from "~/components/ui/BackgroundPicture";
import { toAnalytics } from "~/sdk/ga4/transform/toAnalytics";
import { toProductItem } from "~/sdk/ga4/transform/toProductItem";
import { getFinalPrice } from "~/sdk/price";
import { type FnContext as FnContext, type SectionProps as SectionProps } from "~/types/deco";
interface ImageProp {
    /**
     * @title Imagem
     */
    src: ImageWidget;
    /**
     * @title Texto Alternativo
     */
    alt: string;
}
export interface Props {
    /**
     * @title Produto Principal
     */
    mainProducts: Product[] | null;
    /**
     * @title Produtos da Prateleira
     */
    products: Product[] | null;
    /**
     * @title Título
     * @format html
     */
    title: HTMLWidget;
    /**
     * @title Ícone
     */
    titleIcon: TitleIcon;
    /**
     * @title Imagem de Fundo
     */
    backgroundImage: ImageProp;
    /**
     * @title Slider infinito
     * @default true
     */
    infinite?: boolean;
    /**
     * @title Agendamento
     * @description O campo "fim" é obrigatório para exibição do contador
     */
    scheduling?: Scheduling;
    ga4?: ViewItemListParams;
}
export const loader = (props: Props, request: Request, ctx: FnContext) => {
    const mainProduct = props.mainProducts?.[0];
    return {
        ...props,
        // Both the carousel cards and the "mainProduct" hero read only what
        // ProductCard reads (price/listPrice/Visa installments/teasers/images),
        // so both go through the shelf trim — see ~/sdk/trimProductForShelf.
        products: props.products?.map(trimProductForShelf) ?? null,
        mainProduct: mainProduct && trimProductForShelf(mainProduct),
        isMobile: ctx.device !== 'desktop',
        isPerformanceTestPage: new URL(request.url).pathname.includes('performance'),
    };
};
function getInitialCountdown(endDate: string) {
    const delta = Math.max(0, Math.round((new Date(endDate).getTime() - Date.now()) / 1000));
    return {
        days: Math.floor(delta / 86400),
        hours: Math.floor(delta / 3600) % 24,
        minutes: Math.floor(delta / 60) % 60,
        seconds: delta % 60,
    };
}
export default function CountdownProductShelf({ mainProduct, products, title, titleIcon, backgroundImage, infinite = true, scheduling, isMobile, ga4, isPerformanceTestPage, }: SectionProps<typeof loader>) {
    if (!scheduling?.end ||
        !isSchedulingActive(scheduling) ||
        !mainProduct ||
        !products?.length) {
        return null;
    }
    const countdown = getInitialCountdown(scheduling.end);
    const offers = mainProduct?.offers;
    const teaser = offers?.offers[0]?.teasers?.find((teaser) => teaser.effects.parameters.some((parameter) => parameter.name === 'PercentualDiscount'));
    const teaserLabel = teaser?.name.replace(/(\d+%)/, "<span class='font-bold'>$1 OFF</span>")
        .replace(/pix/i, 'PIX');
    const sliderId = useId();
    const timerId = useId();
    const productImage = mainProduct.image?.[0].url ?? '';
    const productUrl = mainProduct.isVariantOf?.url ?? mainProduct.url;
    const possibilities = useVariantPossibilities(mainProduct.isVariantOf?.hasVariant ?? [], mainProduct);
    const variantLength = Object.keys(possibilities).length;
    const hasVariants = variantLength > 1 ||
        (variantLength === 1 && Object.keys(Object.values(possibilities)[0]).length > 1);
    const { listPrice = 0, price = 0, installments } = useOffer(mainProduct.offers);
    const productName = hasVariants
        ? mainProduct.isVariantOf?.name ?? mainProduct.name ?? ''
        : mainProduct.name ?? '';
    const brandName = mainProduct.brand?.name;
    const { finalPrice, discountType } = getFinalPrice(offers);
    const viewItemListEvent = toAnalytics({
        type: 'view_item_list',
        data: {
            items: products.map((product, index) => toProductItem(product, { index, quantity: 1 })),
            view: {
                id: ga4?.item_list_id ?? 'countdown_product_shelf',
                name: ga4?.item_list_name ?? stripHTML(title ?? '') ?? 'Prateleira com contador',
            },
        },
    });
    return (<div id={timerId} className={clsx('flex flex-col w-full gap-12 mx-auto lg:gap-16 py-6 lg:py-12 px-4 max-w-[1730px]')}>
            <div className='flex items-center justify-center gap-3 px-3'>
                {title && <Title title={title} icon={titleIcon}/>}
            </div>
            <div className={clsx('flex flex-col items-center gap-[3.75rem] justify-center lg:flex-row', 'lg:gap-[8vw]')}>
                <div className='relative overflow-hidden lg:overflow-visible h-auto min-h-[360px] lg:h-[500px] lg:min-h-0 min-[1366px]:h-[550px] w-full max-w-[500px] lg:max-w-[600px] flex flex-col justify-center pb-3 lg:pb-0 bg-[length:100%_100%] lg:bg-[length:100%_500px] min-[1366px]:bg-[length:100%_550px] bg-no-repeat'>
                    <BackgroundPicture backgroundImage={backgroundImage} />

                    <div className='relative z-10 flex items-center gap-1 text-sm font-bold text-center text-white lg:gap-1 lg:text-2xl mt-12 mb-4 ml-auto mr-[12%] min-[500px]:mr-[12.5%] lg:mr-[9%] xl:mr-[13%] lg:mt-10 lg:mb-20'>
                        <span className='inline-flex items-center justify-center min-w-[2rem] h-8 p-1 lg:min-w-0 lg:h-auto lg:p-2 rounded bg-red-wine leading-[1em]'>
                            <span id={`${timerId}::days`} className='h-[1em] inline-block overflow-y-hidden tabular-nums' suppressHydrationWarning>{String(countdown.days).padStart(2, '0')}</span>
                        </span>
                        <span className='inline-flex items-center justify-center min-w-[2rem] h-8 p-1 lg:min-w-0 lg:h-auto lg:p-2 rounded bg-red-wine leading-[1em]'>
                            <span id={`${timerId}::hours`} className='h-[1em] inline-block overflow-y-hidden tabular-nums' suppressHydrationWarning>{String(countdown.hours).padStart(2, '0')}</span>
                        </span>
                        <span className='inline-flex items-center justify-center min-w-[2rem] h-8 p-1 lg:min-w-0 lg:h-auto lg:p-2 rounded bg-red-wine leading-[1em]'>
                            <span id={`${timerId}::minutes`} className='h-[1em] inline-block overflow-y-hidden tabular-nums' suppressHydrationWarning>{String(countdown.minutes).padStart(2, '0')}</span>
                        </span>
                        <span className='inline-flex items-center justify-center min-w-[2rem] h-8 p-1 lg:min-w-0 lg:h-auto lg:p-2 rounded bg-red-wine leading-[1em]'>
                            <span id={`${timerId}::seconds`} className='h-[1em] inline-block overflow-y-hidden tabular-nums' suppressHydrationWarning>{String(countdown.seconds).padStart(2, '0')}</span>
                        </span>
                    </div>

                    <div className='relative z-10 flex h-auto min-h-[160px] lg:h-[175px] items-center justify-center gap-x-2 lg:gap-x-8 mx-5 sm:mx-6 lg:mx-8 max-w-full box-border'>
                        <a href={productUrl && relative(productUrl)} aria-label='Ver produto' className='grid place-content-center shrink-0'>
                            <Image src={productImage!} className='aspect-square max-lg:w-[105px] max-lg:h-[105px]' width={190} height={190} alt={productName} loading='lazy'/>
                        </a>

                        <div className='flex flex-col items-center text-center gap-1 w-[min(165px,42%)] lg:w-[150px] lg:gap-0 min-w-0 shrink'>
                            <a href={productUrl && relative(productUrl)} title={productName} className='w-full shrink-0 overflow-hidden'>
                                <h2 className='text-sm font-bold leading-tight line-clamp-2 text-neutral-600 lg:line-clamp-3 lg:h-14 lg:overflow-hidden' dangerouslySetInnerHTML={{ __html: productName }}/>
                            </a>
                            {brandName && (<div className='flex items-center justify-center text-center shrink-0'>
                                    <a href={`/${urlify(brandName)}?map=b`} className='inline-block py-1 text-xs leading-none text-neutral-600'>
                                        {brandName}
                                    </a>
                                </div>)}

                            <div className='flex items-center justify-center text-xs text-white w-full max-w-full lg:min-h-[25px] mx-auto lg:mt-2 shrink-0'>
                                {teaserLabel && (<p className='w-full max-w-full h-full py-1 px-1 rounded-[5px] bg-[#361318] truncate' dangerouslySetInnerHTML={{ __html: teaserLabel }}/>)}
                            </div>
                            <div className='hidden lg:block h-3 shrink-0'/>
                            <a href={productUrl && relative(productUrl)} className='w-full overflow-hidden shrink-0'>
                                <div className='flex flex-col justify-center gap-0'>
                                    <div className='text-xs line-through text-neutral-400 whitespace-nowrap'>
                                        De{' '}
                                        {formatPrice(listPrice, mainProduct.offers?.priceCurrency)}
                                    </div>
                                    <div className='text-base lg:text-lg font-bold leading-tight text-salmon whitespace-nowrap'>
                                        {formatPrice(finalPrice, mainProduct.offers?.priceCurrency)}
                                        {discountType === 'pix' && ' no pix'}
                                    </div>
                                </div>
                                {installments && (<div className='text-xs text-neutral-600 whitespace-nowrap'>
                                        <p dangerouslySetInnerHTML={{ __html: installments }}/>
                                    </div>)}
                            </a>
                            <div className='h-auto lg:h-4 shrink-0'>
                                {teaser && (<p className='font-medium text-xs text-center whitespace-nowrap lg:mt-2'>
                                        <span className='font-bold text-[13px] capitalize'>
                                            {formatPrice(price, offers?.priceCurrency)}
                                        </span>{' '}
                                        a prazo
                                    </p>)}
                            </div>
                        </div>
                    </div>
                </div>
                <div id={sliderId} className='flex items-center relative px-4 lg:px-0 gap-1 lg:w-[40%] max-w-[min(100vw,600px)] lg:mr-10'>
                    <Slider className='carousel col-span-full gap-[1.1rem] row-span-full lg:mx-[18px]'>
                        {products?.map((product, index) => (<Slider.Item index={index} className='carousel-item' style={{ width: 'calc(50% - 0.625rem)' }}>
                                <ProductCard product={product} itemListId={ga4?.item_list_id ?? 'countdown_product_shelf'} itemListName={ga4?.item_list_name ?? stripHTML(title ?? '') ??
                'Prateleira com contador'} isMobile={isMobile} variant='Countdown-home' index={index} isPerformanceTestPage={isPerformanceTestPage}/>
                            </Slider.Item>))}
                    </Slider>
                    <>
                        <div className='absolute left-0 lg:-left-[3rem] z-[1]'>
                            <Slider.PrevButton className='btn btn-circle bg-opacity-80 text-salmon active:text-black active:bg-[#e1dfdb] active:bg-opacity-80 lg:hover:text-black bg-[#e1dfdb] lg:hover:bg-[#e1dfdb] lg:hover:bg-opacity-80 border-none [&_svg]:text-[#e5554f] w-[3.4375rem] h-[3.4375rem]'>
                                <Icon size={36} id='BannerChevronLeft' strokeWidth={3}/>
                            </Slider.PrevButton>
                        </div>
                        <div className='absolute right-0 lg:-right-[3rem] z-[1]'>
                            <Slider.NextButton className='btn btn-circle bg-opacity-80 text-salmon active:text-black active:bg-[#e1dfdb] active:bg-opacity-80 lg:hover:text-black bg-[#e1dfdb] lg:hover:bg-[#e1dfdb] lg:hover:bg-opacity-80 border-none [&_svg]:text-[#e5554f] w-[3.4375rem] h-[3.4375rem]'>
                                <Icon size={36} id='BannerChevronRight' strokeWidth={3}/>
                            </Slider.NextButton>
                        </div>
                    </>
                    <SliderJS rootId={sliderId} infinite={infinite}/>
                </div>
            </div>

            <TimerJS rootId={timerId} expiresAt={scheduling!.end}/>

            <SendEventOnView id={sliderId} event={viewItemListEvent}/>
        </div>);
}
